import { useEffect, useState } from "react";
import FollowUpAutomateToggle from "./FollowUpAutomateToggle";
// Ported from Frontend commit 2b48b63a (Callback Scheduling Limit row) — Input/Button needed for the
// new maxCallbackHorizonDays field below.
import { Input } from "V2.0/common/input";
import { Button } from "V2.0/common/button";

import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

function AutomatedCallFollowUpInfo() {
  return (
    <div className="flex gap-x-2 items-center">
      <ExclamationTriangleIcon className="text-orange-500 size-4" />
      <p className="font-light text-xs text-orange-500">
        Applicable to Outbound Call Campaigns
      </p>
    </div>
  );
}

export default function FollowUpAutomate(props) {
  // Old: const { automatedFollowUpEnabled, onAutomatedFollowUpToggle, disabled } = props;
  // Replaced to add the callback-horizon props ported from Frontend commit 2b48b63a. `disabled` kept
  // (not renamed to Frontend's `isViewOnly`) since that's Catalyst's existing prop name from FollowUps.tsx.
  const {
    automatedFollowUpEnabled,
    onAutomatedFollowUpToggle,
    disabled,
    maxCallbackHorizonDays,
    onMaxCallbackHorizonCommit,
    maxCallbackHorizonMin,
    maxCallbackHorizonMax,
  } = props;

  // Local mirror so the field can be empty or partially typed; the committed value comes back down
  // through props once saved.
  const [horizonInput, setHorizonInput] = useState(String(maxCallbackHorizonDays));

  useEffect(() => {
    setHorizonInput(String(maxCallbackHorizonDays));
  }, [maxCallbackHorizonDays]);

  // Drives the Save button. Compared as strings so re-typing the same number does not arm it, and so
  // an empty field counts as dirty rather than silently matching.
  const isDirty = horizonInput !== String(maxCallbackHorizonDays);

  // The `max` attribute on a number input only bounds the spinner arrows and native form
  // validation — it does not stop typing, so "500" went in happily and was then silently rewritten
  // to 30 on save. Reject the keystroke instead, so the field can never display a value that is not
  // the one that will be stored.
  //
  // Rejecting rather than clamping on each keystroke is deliberate: clamping would turn "45" into
  // "30" the instant the second digit landed, replacing what was typed with an unrelated number.
  // Refusing the digit leaves "4" on screen, which is the user's own input.
  //
  // An empty field is allowed through so the value can be cleared and retyped; the lower bound is
  // applied at commit, since blocking "0" would also block the first keystroke of nothing useful
  // while making legitimate editing awkward.
  const handleHorizonChange = (rawValue) => {
    if (rawValue === "") {
      setHorizonInput("");
      return;
    }
    if (!/^\d+$/.test(rawValue)) return;
    if (Number(rawValue) > maxCallbackHorizonMax) return;
    setHorizonInput(rawValue);
  };

  return (
    <div className="flex flex-col gap-y-4 border rounded-lg px-4 pt-4 pb-4">
      <div className="flex justify-between">
        <div className="flex flex-col gap-y-2">
          <div className="flex gap-x-2 items-center">
            <p className="font-semibold text-sm">Automated Callbacks</p>
            <AutomatedCallFollowUpInfo />
          </div>
          <p className="text-xs font-light">
            If enabled, a callback will be scheduled as per the customer request
          </p>
        </div>
        <FollowUpAutomateToggle
          isEnabled={automatedFollowUpEnabled}
          onToggle={onAutomatedFollowUpToggle}
          disabled={disabled}
        />
      </div>

      {/* Only shown when the feature is on — the limit has no effect otherwise, and offering it
          under a disabled toggle invites people to set a number that does nothing. */}
      {automatedFollowUpEnabled && (
        <div className="flex justify-between items-center border-t pt-4">
          <div className="flex flex-col gap-y-1">
            <p className="font-semibold text-sm">Callback Scheduling Limit</p>
            <p className="text-xs font-light">
              Callbacks requested further ahead than this are not scheduled. Maximum{" "}
              {maxCallbackHorizonMax} days.
            </p>
          </div>
          <div className="flex items-center gap-x-2 shrink-0">
            <div className="w-16">
              <Input
                type="number"
                min={maxCallbackHorizonMin}
                max={maxCallbackHorizonMax}
                step={1}
                value={horizonInput}
                disabled={disabled}
                onChange={(e) => handleHorizonChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isDirty) onMaxCallbackHorizonCommit(horizonInput);
                }}
              />
            </div>
            <span className="text-xs text-gray-500">days</span>
            {/* Explicit save rather than committing on blur: a value that persists itself the moment
                focus leaves the field gives no signal anything happened, and clicking away mid-edit
                would silently store a half-typed number.
                ALWAYS rendered — mounting it only while dirty made the row change width the instant
                you typed, pushing the input sideways. Disabled-when-clean keeps the layout fixed.
                `outline` rather than solid: a black button beside a small number field reads as the
                card's primary action, which it is not. */}
            <Button
              outline
              onClick={() => onMaxCallbackHorizonCommit(horizonInput)}
              disabled={!isDirty || disabled}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
